import { initContract } from '@ts-rest/core';
import { z } from 'zod';

export const variantPayloadSchema = z.object({
  name: z.string(),
  payload: z
    .object({
      type: z.string(),
      value: z.string(),
    })
    .optional(),
});
export type VariantPayloadSchema = z.infer<typeof variantPayloadSchema>;

export const featureFlagEnvironmentSchema = z.object({
  name: z.string(),
  type: z.string(),
  enabled: z.boolean(),
  sortOrder: z.number(),
  variantCount: z.string(),
  lastSeenAt: z.string().nullable(),
  hasStrategies: z.boolean().nullable(),
  hasEnabledStrategies: z.boolean().nullable(),
});
export type FeatureFlagEnvironmentSchema = z.infer<typeof featureFlagEnvironmentSchema>;

export const featureFlagTagSchema = z.object({
  value: z.string(),
  type: z.string(),
  color: z.string().nullable(),
});
export type FeatureFlagTagSchema = z.infer<typeof featureFlagTagSchema>;

export const featureFlagDetailsForListSchema = z.object({
  name: z.string(),
  type: z.string(),
  enabled: z.boolean(),
  description: z.string().nullable(),
  stale: z.boolean(),
  favorite: z.boolean(),
  impressionData: z.boolean(),
  createdAt: z.string(),
  environments: z.array(featureFlagEnvironmentSchema),
  lastSeenAt: z.string().nullable(),
  tags: z.array(featureFlagTagSchema),
});
export type FeatureFlagDetailsForListSchema = z.infer<typeof featureFlagDetailsForListSchema>;

export const featureFlagStrategyConstraintSchema = z.object({
  contextName: z.string(),
  operator: z.enum([
    'NOT_IN',
    'IN',
    'STR_ENDS_WITH',
    'STR_STARTS_WITH',
    'STR_CONTAINS',
    'NUM_EQ',
    'NUM_GT',
    'NUM_GTE',
    'NUM_LT',
    'NUM_LTE',
    'DATE_AFTER',
    'DATE_BEFORE',
    'SEMVER_EQ',
    'SEMVER_GT',
    'SEMVER_LT',
    'REGEX',
  ]),
  caseInsensitive: z.boolean().nullish(),
  inverted: z.boolean().nullish(),
  values: z.array(z.string()).nullish(),
  value: z.string().nullish(),
});
export type FeatureFlagStrategyConstraintSchema = z.infer<typeof featureFlagStrategyConstraintSchema>;

export const featureFlagStrategyVariantSchema = z.object({
  name: z.string(),
  weight: z.number(),
  weightType: z.enum(['variable', 'fix']),
  stickiness: z.string(),
  payload: variantPayloadSchema.shape.payload.nullable(),
});
export type FeatureFlagStrategyVariantSchema = z.infer<typeof featureFlagStrategyVariantSchema>;

export const featureFlagVariantVariantSchema = featureFlagStrategyVariantSchema.extend({
  overrides: z
    .array(
      z.object({
        contextName: z.string(),
        values: z.array(z.string()),
      }),
    )
    .nullable(),
});
export type FeatureFlagVariantVariantSchema = z.infer<typeof featureFlagVariantVariantSchema>;

export const featureFlagStrategiesSchema = z.object({
  name: z.string(),
  id: z.string().nullable(),
  title: z.string().nullable(),
  disabled: z.boolean().nullable(),
  featureName: z.string().nullable(),
  sortOrder: z.number().nullable(),
  segments: z.array(z.number()).nullable(),
  constraints: z.array(featureFlagStrategyConstraintSchema).nullable(),
  variants: z.array(featureFlagStrategyVariantSchema),
  parameters: z.record(z.string(), z.string()).nullable(),
});
export type FeatureFlagStrategiesSchema = z.infer<typeof featureFlagStrategiesSchema>;

export const milestoneStrategy = z.object({
  id: z.string(),
  milestoneId: z.string(),
  sortOrder: z.number(),
  strategyName: z.string(),
  title: z.string().nullable(),
  parameters: z.record(z.string(), z.string()).nullable(),
  constraints: z.array(featureFlagStrategyConstraintSchema).nullable(),
  variants: z.array(featureFlagStrategyVariantSchema).nullable(),
  segments: z.array(z.number()).nullable(),
});
export type MilestoneStrategy = z.infer<typeof milestoneStrategy>;

export const strategySegmentSchema = z.object({
  id: z.number().int().gte(0),
  name: z.string(),
  constraints: z.array(featureFlagStrategyConstraintSchema),
  createdAt: z.string(),
  description: z.string().nullable(),
  usedInFeatures: z.number().int().gte(0).nullable(),
  usedInProjects: z.number().int().gte(0).nullable(),
  project: z.string().nullable(),
  createdBy: z.string().nullable(),
});
export type StrategySegmentSchema = z.infer<typeof strategySegmentSchema>;

export const milestoneSchema = z.object({
  id: z.string(),
  name: z.string(),
  sortOrder: z.number(),
  releasePlanDefinitionId: z.string(),
  startedAt: z.string().nullable(),
  transitionCondition: z
    .object({
      intervalMinutes: z.number(),
    })
    .nullable(),
  progressionExecutedAt: z.string().nullable(),
  pausedAt: z.string().nullable(),
  strategies: z.array(milestoneStrategy).nullable(),
});
export type MilestoneSchema = z.infer<typeof milestoneSchema>;

export const safeguardSchema = z.object({
  id: z.string(),
  action: z.object({
    type: z.string(),
    id: z.string(),
  }),
  triggerCondition: z.object({
    operator: z.enum(['>', '<']),
    threshold: z.number(),
  }),
  impactMetric: z.object({
    id: z.string(),
    metricName: z.string(),
    timeRange: z.enum(['hour', 'day', 'week', 'month']),
    aggregationMode: z.enum(['rps', 'count', 'avg', 'sum', 'p95', 'p99', 'p50']),
    labelSelectors: z.record(z.string(), z.array(z.string())),
  }),
});
export type SafeguardSchema = z.infer<typeof safeguardSchema>;

export const releasePlanSchema = z.object({
  id: z.string(),
  discriminator: z.enum(['plan']),
  name: z.string(),
  featureName: z.string(),
  environment: z.string(),
  createdByUserId: z.number(),
  createdAt: z.string(),
  milestones: z.array(milestoneSchema),
  releasePlanTemplateId: z.string(),
  description: z.string().nullable(),
  activeMilestoneId: z.string().nullable(),
  safeguards: z.array(safeguardSchema).nullable(),
});
export type ReleasePlanSchema = z.infer<typeof releasePlanSchema>;

export const featureFlagDetailsEnvironmentSchema = z.object({
  name: z.string(),
  enabled: z.boolean(),
  featureName: z.string().nullable(),
  environment: z.string().nullable(),
  type: z.string().nullable(),
  sortOrder: z.number().nullable(),
  variantCount: z.number().nullable(),
  strategies: z.array(featureFlagStrategiesSchema).nullable(),
  variants: z.array(featureFlagVariantVariantSchema).nullable(),
  changeRequestIds: z.array(z.number()).nullable(),
  milestoneName: z.string().nullable(),
  milestoneOrder: z.number().nullable(),
  totalMilestones: z.number().nullable(),
  lastSeenAt: z.string().nullable(),
  hasStrategies: z.boolean().nullable(),
  hasEnabledStrategies: z.boolean().nullable(),
  releasePlans: z.array(releasePlanSchema).nullable(),
  yes: z.number().nullable(),
  no: z.number().nullable(),
});
export type FeatureFlagDetailsEnvironmentSchema = z.infer<typeof featureFlagDetailsEnvironmentSchema>;

export const userInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  imageUrl: z.string(),
});
export type UserInfoSchema = z.infer<typeof userInfoSchema>;

export const featureFlagDetailsSchema = z.object({
  name: z.string(),
  type: z.string().nullable(),
  description: z.string().nullable(),
  archived: z.boolean().nullable(),
  project: z.string().nullable(),
  enabled: z.boolean().nullable(),
  stale: z.boolean().nullable(),
  favorite: z.boolean().nullable(),
  impressionData: z.boolean().nullable(),
  createdAt: z.string().nullable(),
  createdBy: userInfoSchema.nullable(),
  archivedAt: z.string().nullable(),
  environments: z.array(featureFlagDetailsEnvironmentSchema),
  tags: z.array(featureFlagTagSchema),
  children: z.array(z.string()).nullable(),
  lifecycle: z
    .object({
      stage: z.enum(['initial', 'pre-live', 'live', 'completed', 'archived']),
      enteredStageAt: z.string(),
      status: z.string().nullable(),
    })
    .nullable(),
  dependencies: z
    .array(
      z.object({
        feature: z.string(),
        enabled: z.boolean().nullable(),
        variants: z.array(z.string()).nullable(),
      }),
    )
    .nullable(),
  collaborators: z
    .object({
      users: z.array(userInfoSchema),
    })
    .nullable(),
  links: z
    .array(
      z.object({
        id: z.string(),
        url: z.string(),
        title: z.string().nullable(),
        feature: z.string().nullable(),
      }),
    )
    .nullable(),
  lastSeenAt: z.string().nullable(),
  variants: z.array(featureFlagVariantVariantSchema).nullable(),
});
export type FeatureFlagDetailsSchema = z.infer<typeof featureFlagDetailsSchema>;

export const featureFlagListSchema = z.object({
  version: z.number(),
  features: z.array(featureFlagDetailsForListSchema),
});
export type FeatureFlagListSchema = z.infer<typeof featureFlagListSchema>;

export const featureFlagVariantsSchema = z.object({
  version: z.number(),
  variants: z.array(featureFlagVariantVariantSchema),
});
export type FeatureFlagVariantsSchema = z.infer<typeof featureFlagVariantsSchema>;

const mobileGTVUnleashAdminAppNameSchema = z.enum(['galateatv', 'herdrama', 'ironblood']);

export const getUnleashAdminRouter = (c: ReturnType<typeof initContract>) =>
  c.router(
    {
      getFlags: {
        summary: 'Gets unleash flags',
        method: 'GET',
        path: '/flags',
        query: z.object({
          appName: mobileGTVUnleashAdminAppNameSchema.optional(),
          includeReleaseFalgs: z.coerce.boolean().optional(),
        }),
        responses: {
          200: z.array(z.object({ name: z.string(), description: z.string(), isEnabled: z.boolean() })),
        },
      },

      getVariant: {
        summary: 'Gets unleash variant',
        method: 'GET',
        path: '/flags/:flagName/variants',
        pathParams: c.type<{ flagName: string }>(),
        query: z.object({
          appName: mobileGTVUnleashAdminAppNameSchema.optional(),
        }),
        responses: {
          200: z.array(variantPayloadSchema),
        },
      },

      getAllFlagsDetailed: {
        summary: 'Gets all unleash flags with full environment, strategy, and variant details',
        method: 'GET',
        path: '/flags/detailed',
        query: z.object({
          includeReleaseFlags: z.coerce.boolean().optional(),
        }),
        responses: {
          200: z.array(featureFlagListSchema.shape.features),
        },
      },
    },
    {
      pathPrefix: '/admin',
    },
  );

export const acquisitionSurveyOptionSchema = z.object({
  key: z.string(),
  label: z.record(z.string(), z.string()),
  // When true, selecting this option opens the follow-up page instead of submitting.
  // New optional field — existing payloads without it remain valid (treated as terminal).
  opensFollowup: z.boolean().optional(),
});

export const acquisitionSurveyPayloadSchema = z.object({
  options: z.array(acquisitionSurveyOptionSchema),
  // New optional field — options shown on the follow-up page when an `opensFollowup` option
  // is selected on the main page. If omitted, no follow-up page is shown (legacy behavior).
  followupOptions: z.array(acquisitionSurveyOptionSchema).optional(),
  includeOther: z.boolean(),
  // New optional field — when true, an "I'm Not Sure" pill is pinned penultimate on both pages.
  includeNotSure: z.boolean().optional(),
});

export type AcquisitionSurveyPayload = z.infer<typeof acquisitionSurveyPayloadSchema>;
export type AcquisitionSurveyOption = z.infer<typeof acquisitionSurveyOptionSchema>;
